import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALGROUPFINANCE_LOAD_BY_TRIAL_GROUP_ID,
    ACTION_TRIALGROUPFINANCE_LOAD_BY_ID,
    ACTION_TRIALGROUPFINANCE_LOAD_FAILURE,
    ACTION_TRIALGROUPFINANCE_LOAD_SUCCESS,

    ACTION_TRIALGROUPFINANCE_SAVE,
    ACTION_TRIALGROUPFINANCE_SAVE_SUCCESS,
    ACTION_TRIALGROUPFINANCE_SAVE_FAILURE,

    ACTION_TRIALGROUPFINANCE_VALIDATE,
    ACTION_TRIALGROUPFINANCE_VALIDATE_SUCCESS,
    ACTION_TRIALGROUPFINANCE_VALIDATE_FAILURE,

    ACTION_TRIALGROUPFINANCE_CREATE,
    ACTION_TRIALGROUPFINANCE_CREATE_SUCCESS,
    ACTION_TRIALGROUPFINANCE_CREATE_FAILURE,

    ACTION_TRIALGROUPFINANCE_SYNC,
    ACTION_TRIALGROUPFINANCE_SYNC_SUCCESS,
    ACTION_TRIALGROUPFINANCE_SYNC_FAILURE,

    ACTION_TRIALGROUPFINANCE_CLEAR,
    ACTION_TRIALGROUPFINANCE_FILTER,

    TrialGroupFinanceAction
} from '../actions/trialGroupFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialGroupFinanceState {
    formData: Dtos.TrialGroupFinance | Dtos.TrialGroupFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialGroupFinance[];
    formLength: number;
}

const initialState: ITrialGroupFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialGroupFinanceState, _isLoading: boolean): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialGroupFinanceState,
    formData: Dtos.TrialGroupFinance | Dtos.TrialGroupFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialGroupFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialGroupFinanceState, formFilter: IFormFilter): ITrialGroupFinanceState {

    const items: Dtos.TrialGroupFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.accountNumber.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialGroupFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialGroupFinanceState, propertiesToClear?: string[]): ITrialGroupFinanceState {

    if (!propertiesToClear) {
        const newState: ITrialGroupFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialGroupFinanceState, _isSaving: boolean): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialGroupFinanceState,
    formData: Dtos.TrialGroupFinance | Dtos.TrialGroupFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialGroupFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialGroupFinanceState, _isValidating: boolean): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialGroupFinanceState,
    formData: Dtos.TrialGroupFinance | Dtos.TrialGroupFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialGroupFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialGroupFinanceState, _isCreating: boolean): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialGroupFinanceState,
    formData: Dtos.TrialGroupFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialGroupFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupFinanceState {
    const newState: ITrialGroupFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialGroupFinanceReducer: Reducer<ITrialGroupFinanceState> = (state: ITrialGroupFinanceState = initialState, action: TrialGroupFinanceAction) => {
    switch (action.type) {
        case ACTION_TRIALGROUPFINANCE_LOAD_BY_TRIAL_GROUP_ID:
        case ACTION_TRIALGROUPFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALGROUPFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALGROUPFINANCE_SAVE:
            return saving(state, true);
        case ACTION_TRIALGROUPFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALGROUPFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_CREATE:
            return creating(state, true);
        case ACTION_TRIALGROUPFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALGROUPFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALGROUPFINANCE_SYNC:
            return loading(state, true);
        case ACTION_TRIALGROUPFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialGroupFinanceReducer;